import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { OrderCard } from '../../components/nursery';
import { ShoppingCart, Plus, FileUp } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useStockSeat } from '../../hooks/useStockSeat';
import type { NurseryOrder, NurseryOrderStatus } from '../../types/nursery';

// Labels in the locale file, under tab.<value>.
const STATUS_TABS: { value: NurseryOrderStatus | 'all' }[] = [
  { value: 'all' },
  { value: 'draft' },
  { value: 'confirmed' },
  { value: 'allocated' },
  { value: 'partially_allocated' },
  { value: 'picking' },
  { value: 'picked' },
  { value: 'shipped' },
  { value: 'invoiced' },
  { value: 'completed' },
  { value: 'cancelled' },
];

function OrderList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderList' });
  const navigate = useNavigate();
  // The order sheet saves through manager/admin routes.
  const { hasAccess: canManage } = useStockSeat('manage');
  const [orders, setOrders] = useState<NurseryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<NurseryOrderStatus | 'all'>('all');
  // In the URL so the order page's "Backorders" link can land on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const backordersOnly = searchParams.get('backorders') === 'true';
  const toggleBackorders = () => {
    const next = new URLSearchParams(searchParams);
    if (backordersOnly) next.delete('backorders'); else next.set('backorders', 'true');
    setSearchParams(next, { replace: true });
  };

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { status?: string; backorders?: boolean } = {};
      if (activeStatus !== 'all') filters.status = activeStatus;
      if (backordersOnly) filters.backorders = true;
      const res = await nurseryService.getOrders(filters);
      if (res.success) {
        setOrders(res.data || []);
      }
    } catch (e: any) {
      setError(e?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus, backordersOnly]);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/orders/import')}>
              <FileUp className="h-4 w-4" />
              {t('import')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/nursery/orders/new')}>
              <Plus className="h-4 w-4" />
              {t('newOrder')}
            </Button>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Status filter tabs */}
        <div className="flex gap-1 flex-wrap mb-6">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveStatus(tab.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeStatus === tab.value
                ? 'bg-primary-600 text-white font-medium'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            >
              {t(`tab.${tab.value}`)}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={backordersOnly} onChange={toggleBackorders} className="rounded border-neutral-300" />
            {t('backordersOnly')}
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('empty')}</p>
            <p className="text-sm mt-1">
              {activeStatus !== 'all' || backordersOnly ? t('emptyFiltered') : t('emptyFirst')}
            </p>
            {activeStatus === 'all' && !backordersOnly && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/nursery/orders/new')}>
                <Plus className="h-4 w-4" /> {t('newOrder')}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orders.map(order => (
              <OrderCard key={order.id} order={order} canEdit={canManage} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default OrderList;
